import Vue from 'vue'
import * as VTable from '@visactor/vtable'
import { Message } from 'element-ui'
import {
  generateOrderedRandomNumbersFast,
  strNumAdd,
  compareNumericStrings
} from './generateOrderedRandomNumbers'

// 递归树结构
const dfsFn = (arr, fn, children = 'children') => {
  arr.forEach(e => {
    fn(e)
    if (e[children]) dfsFn(e[children], fn)
  })
}

// 复制内容
const copyCells = (tableInstance, isAll, hasNo) => {
  let str
  if (isAll) {
    const arr = Array.isArray(tableInstance)
      ? tableInstance.map(e => e.getAllBodyCells()).flat()
      : tableInstance.getAllBodyCells()
    str = arr
      .map(cells =>
        cells
          .filter((e, index) => (hasNo ? true : index > 0))
          .map(e => e.dataValue)
          .join('\t')
      )
      .join('\n') // 换行
  } else {
    str = tableInstance.getCopyValue()
  }
  copy(str)
}

// 剪切板复制
const copy = str => {
  if (navigator.clipboard && window.isSecureContext) {
    // 使用现代 Clipboard API
    navigator.clipboard.writeText(str)
  } else {
    // 降级方案：使用 textarea 保持换行符
    const textarea = document.createElement('textarea')
    textarea.value = str
    textarea.style.position = 'fixed'
    textarea.style.left = '-999999px'
    textarea.style.top = '-999999px'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
  }
}

function tranBoolean(str) {
  if (str === 'true') return true
  if (str === 'false') return false
  return str
}

// 获取剪切板的内容
const paste = async () => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      // 使用现代 Clipboard API
      const text = await navigator.clipboard.readText()
      return text
    } else {
      // 降级方案：提示用户手动粘贴
      return new Promise(resolve => {
        const textarea = document.createElement('textarea')
        textarea.style.position = 'fixed'
        textarea.style.left = '-999999px'
        textarea.style.top = '-999999px'
        document.body.appendChild(textarea)
        textarea.focus()

        // 监听粘贴事件
        const handlePaste = e => {
          e.preventDefault()
          const clipboardData = e.clipboardData || window.clipboardData
          const pastedText = clipboardData.getData('text')
          document.body.removeChild(textarea)
          textarea.removeEventListener('paste', handlePaste)
          resolve(pastedText)
        }

        textarea.addEventListener('paste', handlePaste)
      })
    }
  } catch (error) {
    console.error('获取剪切板内容失败:', error)
    return ''
  }
}

function filterTable(record, form) {
  for (const [key, val] of Object.entries(form)) {
    if (val === undefined) continue
    const str = typeof record[key] === 'number' ? record[key] + '' : record[key]
    if (!str?.includes(val)) return false
  }
  return true
}

// 前执行防抖函数
const debounce = (fn, delay = 100) => {
  let timer
  return function (...args) {
    if (!timer) {
      fn.apply(this, args)
      timer = setTimeout(() => {
        timer = null
      }, delay)
    }
  }
}

// 后执行防抖函数
const debounceAfter = (fn, delay = 100) => {
  let timer
  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      fn.apply(this, args)
    }, delay)
  }
}

/**
 * 选择文件的通用方法
 * @param {Object} options - 配置选项
 * @param {string} options.accept - 接受的文件类型，默认为'.xlsx,.xls'
 * @param {boolean} options.multiple - 是否允许多选，默认为false
 * @param {Array} options.allowedTypes - 允许的MIME类型数组
 * @param {number} options.maxSize - 最大文件大小（字节），默认为10MB
 * @param {Function} options.onSuccess - 文件选择成功的回调函数
 * @param {Function} options.onError - 错误处理回调函数
 * @returns {Promise} 返回Promise，resolve时传入选中的文件
 */
function selectFile(options = {}) {
  const {
    accept = options.accept,
    multiple = false,
    maxSize = 50 * 1024 * 1024, // 10MB
    onSuccess,
    onError
  } = options

  return new Promise((resolve, reject) => {
    // 创建隐藏的文件输入元素
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept
    input.multiple = multiple

    // 监听文件选择事件
    input.onchange = event => {
      const files = event.target.files
      if (files && files.length > 0) {
        // 验证文件大小
        for (const file of files) {
          if (maxSize && file.size > maxSize) {
            const error = new Error(
              `文件大小不能超过${Math.round(maxSize / 1024 / 1024)}MB`
            )
            if (onError) onError(error)
            reject(error)
            return
          }
        }

        const result = multiple ? files : files[0]
        // 成功选择文件
        if (onSuccess) onSuccess(result)
        resolve(result)
      } else {
        // 用户取消选择
        reject(new Error('用户取消选择文件'))
      }
    }

    // 触发文件选择对话框
    input.click()
  })
}

// 在2个数字之间生成多个随机数
function setRecordsSerialNumber(records, leftNum, rightNum) {
  if (typeof leftNum === 'number') leftNum = leftNum + ''
  if (typeof rightNum === 'number') rightNum = rightNum + ''
  if (!rightNum) {
    // 说明是尾部追加
    records.forEach((e, i) => (e.orderNum = strNumAdd(leftNum ?? '0', i + 1)))
  } else {
    if (rightNum === '0') {
      records.forEach((e, index) => (e.orderNum = '0'))
    } else {
      const serialNumbers = buildSerialNumber(leftNum, rightNum, records.length)
      records.forEach((e, index) => (e.orderNum = serialNumbers[index]))
    }
  }
}

// 在2个数字之间生成多个有序的随机数
function buildSerialNumber(leftNum, rightNum, count) {
  return generateOrderedRandomNumbersFast(leftNum, rightNum, count)
}

// 表格排序
function sortRecords(records, totalKey) {
  if (!records.length) return records
  if (totalKey) {
    const obj = records.find(e => e[totalKey] === '合计')
    if (obj) obj.orderNum = MAXNUM
  }
  dfsFn(records, e => {
    e.children?.sort((a, b) =>
      compareNumericStrings(
        String(a.orderNum || '0'),
        String(b.orderNum || '0')
      )
    )
  })
  records.sort((a, b) =>
    compareNumericStrings(String(a.orderNum || '0'), String(b.orderNum || '0'))
  )
  return records
}

// 判断单元格是不是对象
function cellIsObject(cell) {
  const idKey = cell.field.replace(/Name$/, 'Id')
  if (cell.field.endsWith('Name') && Object.hasOwn(cell.originData, idKey)) {
    return idKey
  }
  return null
}

/**
 * 创建可监听的 Map 对象
 * @param {Function} onChange - 变化时的回调函数
 * @returns {Map} 代理后的 Map 对象
 */
function createWatchableMap(onChange) {
  const map = new Map()

  return new Proxy(map, {
    get(target, prop) {
      const value = target[prop]

      // 如果是函数，需要代理函数调用
      if (typeof value === 'function') {
        return function (...args) {
          const result = value.apply(target, args)

          // 监听会改变 Map 的方法
          switch (prop) {
            case 'set':
              const [key, newValue] = args
              const oldValue = target.get(key)
              onChange('set', key, newValue, oldValue)
              break
            case 'delete':
              const [deleteKey] = args
              const deletedValue = target.get(deleteKey)
              if (result) {
                // delete 返回 boolean，成功才触发
                onChange('delete', deleteKey, undefined, deletedValue)
              }
              break
            case 'clear':
              onChange('clear', undefined, undefined, new Map(target))
              break
          }

          return result
        }
      }

      return value
    }
  })
}

// 列表转树
function listToTree(list, parentId) {
  const arr = list.filter(item => item.parentId === parentId || !item.parentId)
  const arr2 = list.filter(
    item => !(item.parentId === parentId || !item.parentId)
  )
  return arr.map(item => ({
    ...item,
    children: listToTree(arr2, item.id)
  }))
}

// 树转列表
function treeToList(tree) {
  const list = []
  const stack = [...tree]
  while (stack.length) {
    const node = stack.shift()
    list.push(node)
    if (node.children) {
      stack.unshift(...node.children)
      delete node.children
    }
  }
  return sortRecords(list)
}

const MAXNUM = '9999999999'

const aggregationSUM = {
  aggregationType: VTable.TYPES.AggregationType.NONE,
  showOnTop: false,
  formatFun(value) {
    return '合计'
  }
}

const aggregationAve = {
  aggregationType: VTable.TYPES.AggregationType.CUSTOM,
  showOnTop: false,
  cellType: 'text',
  formatFun(value) {
    return '平均值'
  }
}

// 平均值
const aggregationAverage = {
  aggregationType: VTable.TYPES.AggregationType.AVG,
  showOnTop: false,
  formatFun(value) {
    return value ? value.toFixed(2) : ''
  }
}

const aggregationAmount = [
  {
    showOnTop: false,
    aggregationType: VTable.TYPES.AggregationType.SUM,
    formatFun(value) {
      return value.toFixed(2)
    }
  }
]

function aggregationAmountCustom(getFlagFunction) {
  return {
    showOnTop: false,
    aggregationType: VTable.TYPES.AggregationType.CUSTOM,
    aggregationFun(values, records) {
      let sum = 0
      for (let i = 0; i < records.length; i++) {
        const record = records[i]
        if (getFlagFunction(record)) {
          sum += values[i]
        }
      }
      return sum
    },
    formatFun(value) {
      return value.toFixed(2)
    }
  }
}

const defineProperty = (obj, key, val, options) => {
  const {
    enumerable = false,
    configurable = false,
    writable = true
  } = options || {}
  Object.defineProperty(obj, key, {
    value: val,
    enumerable,
    writable,
    configurable
  })
}

async function addCbFn(
  service,
  url,
  getModifyData,
  cleanModifyData,
  fn,
  errFn
) {
  const arr = getModifyData()
  if (!arr.length) return
  let data = [...arr.map(e => ({ ...e, children: undefined }))]
  if (fn) data = fn(data)
  const res = await service.post(url, data)
  if (res?.code === 200) {
    Message.success('保存成功')
    arr.filter(e => !e.id).forEach((e, i) => (e.id = res?.data?.[i]))
  } else {
    if (errFn) errFn()
  }
  cleanModifyData()
}

function getEditModal({
  title,
  key,
  options,
  confirm,
  columns,
  width = '400px',
  allowMultiSelect = false
}) {
  return {
    columns: columns || [{ field: 'name', title }],
    options: options,
    width,
    allowMultiSelect,
    confirm: confirm
      ? confirm
      : (selectedCellInfos, res) => {
          const row = res[0][0].originData
          selectedCellInfos.forEach(cells => {
            const obj = cells[0].originData
            Vue.set(obj, key, row.name)
          })
        }
  }
}

function getOptions(args, canEdit = true) {
  return {
    editor: canEdit
      ? {
          type: 'select',
          options: args.options.map(e => ({ label: e.name, value: e.name }))
        }
      : undefined,
    editModal: getEditModal(args)
  }
}

async function handleImport({
  vm,
  id,
  accept = '',
  multiple = false,
  uploadFn
}) {
  const file = await selectFile({ accept, multiple })
  if (file) return uploadFn ? uploadFn(file) : uploadFile({ vm, file, id })
}

async function uploadFile({ vm, file, id }) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('businessId', id)
  const res = await vm.$service.post('/business-file/upload/' + id, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return res
}

// 预算文件
async function preViewFile(path) {
  if (!path) return
  path = '/file' + path
  window.open(path, '_blank')
}

const imgColumn = key => ({
  cellType(args) {
    if (args.value) return 'image'
    return 'text'
  },
  fieldFormat(record) {
    if (!record[key]) return ''
    return '/file' + record[key]
  }
})

function initCalculation(dependsOn, express, unlisten = []) {
  return {
    dependsOn: dependsOn, // 依赖的字段
    formula(values, record, changedField, evaluateFormula) {
      if (unlisten.includes(changedField)) return
      return evaluateFormula(express, record)
    }
  }
}

const numberStyle = {
  fieldType: 'number',
  headerStyle: {
    textAlign: 'right' // 标题居中
  },
  style: {
    textAlign: 'right' // 内容左对齐（树形结构需要）
  }
}

export {
  dfsFn,
  copyCells,
  copy,
  paste,
  tranBoolean,
  filterTable,
  debounce,
  debounceAfter,
  selectFile,
  setRecordsSerialNumber,
  buildSerialNumber,
  strNumAdd,
  sortRecords,
  cellIsObject,
  createWatchableMap,
  listToTree,
  treeToList,
  MAXNUM,
  aggregationSUM,
  aggregationAmount,
  aggregationAmountCustom,
  aggregationAve,
  aggregationAverage,
  defineProperty,
  addCbFn,
  getEditModal,
  getOptions,
  handleImport,
  preViewFile,
  imgColumn,
  initCalculation,
  numberStyle
}
